// HiBFF — remembered-profile (returning user hint).
//
// Stores ONLY a first name + last-login timestamp in localStorage so the
// /login page can greet a returning user with "Hi {first}, welcome back"
// instead of the generic "Welcome Back" — but ONLY when the user explicitly
// ticked the 30-day "Remember me" box on their last sign-in.
//
// Privacy design:
//   - No email, no last name, no avatar URL, no role, no DOB, no ID.
//   - Cleared on every explicit logout / session lock / idle-timeout
//     (the same paths that already wipe `hibff_user_cache`).
//   - Auto-expires after 30 days so a forgotten device eventually reverts
//     to the neutral "Welcome Back" heading.
//   - The hint NEVER pre-fills the email or password input — the user
//     still has to type their credentials. A bad actor sitting at a shared
//     device cannot use this hint to bypass auth; the worst case is they
//     see a first name they may already know.
//   - Exposed "Not you?" affordance on /login wipes the hint on demand.

const KEY = 'hibff_remembered_profile';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** Save a tiny hint after a remembered login. No-op if rememberMe=false. */
export function saveRememberedProfile(user, rememberMe) {
  try {
    if (!rememberMe || !user || !user.name) return;
    // First token of the display name only. Trim aggressive whitespace and
    // hard-cap length so we never serialise an entire essay into storage.
    const firstName = String(user.name).trim().split(/\s+/)[0].slice(0, 24);
    if (!firstName) return;
    const payload = {
      first_name: firstName,
      last_login_at: Date.now(),
    };
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch (_) {
    /* localStorage may throw in private-mode Safari — silent best-effort */
  }
}

/** Read the hint. Returns null if missing, malformed, or expired. */
export function readRememberedProfile() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.first_name !== 'string') return null;
    if (!parsed.last_login_at || Date.now() - parsed.last_login_at > MAX_AGE_MS) {
      // Expired — clean up.
      localStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
}

/** Explicit clear — called from logout, session-lock, and "Not you?" link. */
export function clearRememberedProfile() {
  try {
    localStorage.removeItem(KEY);
  } catch (_) {
    /* silent */
  }
}
